import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import Button from '../components/ui/Button';
import NetworkVisual from '../components/visuals/NetworkVisual';
import TestimonialCarousel from '../components/sections/TestimonialCarousel';

const GuestPosts = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const trustLogos = [
    "https://media-manager-c.questera.ai/greta-media/22508bb601940b4e20f7d160303c50efd71c7583e6bf992668d180d798243f38c2166642d2b71e0a7892275161f0f512/images/aW1hZ2UvcG5n/b638ef3f0ad2d7b992751683d269680f.png",
    "https://media-manager-c.questera.ai/greta-media/22508bb601940b4e20f7d160303c50efd71c7583e6bf992668d180d798243f38c2166642d2b71e0a7892275161f0f512/images/aW1hZ2UvcG5n/989a4c9b65f6a0d7a5cb7d56d159b937.png",
    "https://media-manager-c.questera.ai/greta-media/22508bb601940b4e20f7d160303c50efd71c7583e6bf992668d180d798243f38c2166642d2b71e0a7892275161f0f512/images/aW1hZ2UvcG5n/7442f7578f4cee335bf8765030e8288f.png",
    "https://media-manager-c.questera.ai/greta-media/22508bb601940b4e20f7d160303c50efd71c7583e6bf992668d180d798243f38c2166642d2b71e0a7892275161f0f512/images/aW1hZ2UvcG5n/04dfb754bd7ef8e2632ead4d51425845.png",
    "https://media-manager-c.questera.ai/greta-media/22508bb601940b4e20f7d160303c50efd71c7583e6bf992668d180d798243f38c2166642d2b71e0a7892275161f0f512/images/aW1hZ2UvcG5n/ce31ad070f0304aebf89c7d20ea4ad5d.png",
    "https://media-manager-c.questera.ai/greta-media/22508bb601940b4e20f7d160303c50efd71c7583e6bf992668d180d798243f38c2166642d2b71e0a7892275161f0f512/images/aW1hZ2UvcG5n/22455314531a78bd82371a51c0c876cf.png",
    "https://media-manager-c.questera.ai/greta-media/22508bb601940b4e20f7d160303c50efd71c7583e6bf992668d180d798243f38c2166642d2b71e0a7892275161f0f512/images/aW1hZ2UvcG5n/a116a6ca393e370b5ded3c10223543b3.png"
  ];

  const marketplaceItems = [
    { site: 'TechCrunch.com', dr: 92, traffic: '12.4M', price: '$1,250', cat: 'Technology', lang: 'EN', tat: '5 Days' },
    { site: 'BusinessInsider.com', dr: 94, traffic: '45.2M', price: '$2,100', cat: 'Business', lang: 'EN', tat: '7 Days' },
    { site: 'Healthline.com', dr: 91, traffic: '85.1M', price: '$1,850', cat: 'Health', lang: 'EN', tat: '4 Days' },
    { site: 'VentureBeat.com', dr: 89, traffic: '4.2M', price: '$950', cat: 'Tech', lang: 'EN', tat: '3 Days' },
  ];

  const faqs = [
    { q: 'Are the websites real?', a: 'Yes, every website in our marketplace is a real, active site with organic traffic. We do not use PBNs or link farms.' },
    { q: 'Can I choose the anchor text?', a: 'Absolutely. You have full control over the anchor text and target URL for every placement you order.' },
    { q: 'How long does publishing take?', a: 'The typical turnaround time is 3 to 7 days, depending on the specific website and content requirements.' },
    { q: 'Do you provide content writing?', a: 'Yes, we have a team of professional writers who can create high-quality, relevant content for your guest posts.' },
    { q: 'Can I filter websites by niche or country?', a: 'Yes, our marketplace features advanced filters for niche, country, language, DR, traffic, and more.' },
    { q: 'Do guest posts include dofollow links?', a: 'The majority of our guest posts include dofollow links, unless specified otherwise in the site listing.' },
    { q: 'Can agencies manage multiple campaigns?', a: 'Yes, our platform is built for scale with agency features to manage multiple clients and campaigns seamlessly.' }
  ];

  return (
    <div className="bg-[#0B0F19] min-h-screen text-slate-300">
      {/* 1. Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden border-b border-white/5">
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
          <div className="absolute top-1/4 -left-1/4 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[120px] animate-pulse"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full opacity-30">
            <NetworkVisual />
          </div>
        </div>

        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            <div className="w-full lg:w-1/2 text-center lg:text-left">
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white leading-[1.1] mb-6 tracking-tight"
              >
                Buy Guest Posts on <br className="hidden md:block" />
                <span className="text-gradient">Real Websites</span>
              </motion.h1>
              <p className="text-lg md:text-xl text-slate-400 mb-10 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                Browse vetted websites across multiple industries and secure placements that build authority, rankings, and visibility.
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start mb-12">
                <Button href="https://app.prboost.io/" variant="primary" className="w-full sm:w-auto !px-10 !py-4.5 text-lg font-bold shadow-[0_20px_50px_rgba(59,130,246,0.3)]">
                  Browse Marketplace
                </Button>
                <Button href="https://app.prboost.io/" variant="secondary" className="w-full sm:w-auto !px-10 !py-4.5 text-lg font-bold">
                  Create Account
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-4 md:gap-8 border-t border-white/5 pt-8 max-w-lg mx-auto lg:mx-0">
                <div>
                  <div className="text-xl md:text-2xl font-bold text-white mb-1">100,000+</div>
                  <div className="text-[10px] md:text-xs font-bold text-slate-500 uppercase tracking-widest">Placements</div>
                </div>
                <div className="border-x border-white/5 px-4 md:px-8">
                  <div className="text-xl md:text-2xl font-bold text-white mb-1">20+</div>
                  <div className="text-[10px] md:text-xs font-bold text-slate-500 uppercase tracking-widest">Niches</div>
                </div>
                <div>
                  <div className="text-xl md:text-2xl font-bold text-white mb-1">Global</div>
                  <div className="text-[10px] md:text-xs font-bold text-slate-500 uppercase tracking-widest">Websites</div>
                </div>
              </div>
            </div>
            <div className="w-full lg:w-1/2 relative lg:-mr-12 xl:-mr-24">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-blue-600/20 blur-[100px] rounded-full opacity-40"></div>
              <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1 }} className="relative z-20">
                <div className="relative rounded-2xl md:rounded-3xl border border-white/10 overflow-hidden shadow-[0_40px_100px_rgba(0,0,0,0.6)] bg-[#0F1423]">
                  <img src="https://media-manager-c.questera.ai/greta-media/57fe1cdd9b663cb2c02a7dabf87358cb277fe0e0f347cf6e941ee58c79db65ed65994d3a49b6ee312e2fb668c3d742bb/images/aW1hZ2UvcG5n/9f84557c862c655646428e661ea4bef0.png" alt="Marketplace" className="w-full h-auto" />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Trust Logos - Faster Infinite Carousel */}
      <section className="py-16 border-b border-white/5 bg-[#070b14]/50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 mb-10 text-center">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-[0.3em]">Publish your story on</p>
        </div>
        <div className="relative w-full overflow-hidden flex items-center">
          <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-[#070B14] to-transparent z-10 pointer-events-none"></div>
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-[#070B14] to-transparent z-10 pointer-events-none"></div>
          
          <motion.div 
            className="flex gap-16 items-center w-max opacity-40 grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-500"
            animate={{ x: ["0%", "-50%"] }}
            transition={{ ease: "linear", duration: 20, repeat: Infinity }}
          >
            {/* Render twice for seamless loop */}
            {[...trustLogos, ...trustLogos].map((logo, idx) => (
              <img 
                key={idx} 
                src={logo} 
                alt="Partner Logo" 
                className="h-8 md:h-10 object-contain px-4" 
              />
            ))}
          </motion.div>
        </div>
      </section>

      {/* 3. Marketplace Preview */}
      <section className="py-24 lg:py-32 relative overflow-hidden bg-[#0B0F19]">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="w-full lg:w-2/5">
              <span className="text-blue-400 font-bold uppercase tracking-[0.2em] text-xs mb-4 block">Marketplace Access</span>
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">Browse Thousands of Guest Post Opportunities</h2>
              <p className="text-slate-400 text-lg mb-8 leading-relaxed">Search and filter websites across multiple industries using transparent SEO metrics, pricing, traffic data, and placement details.</p>
              <ul className="grid grid-cols-2 gap-y-4 gap-x-6 mb-10">
                {['DR / DA Metrics', 'Organic Traffic Data', 'Country & Language Filters', 'Transparent Pricing', 'Multiple Niches', 'Fast Turnaround Times'].map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm font-bold text-white">
                    <SafeIcon icon={FiIcons.FiCheck} className="text-blue-500" /> {f}
                  </li>
                ))}
              </ul>
              <Button href="https://app.prboost.io/" variant="primary" className="!px-8 !py-4">Explore Marketplace</Button>
            </div>
            <div className="w-full lg:w-3/5">
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="glass-card rounded-2xl border-white/10 bg-[#0F1423] shadow-2xl overflow-hidden p-6 md:p-8">
                <div className="flex items-center justify-between mb-8 gap-4">
                  <div className="relative flex-grow">
                    <SafeIcon icon={FiIcons.FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input type="text" placeholder="Search websites..." className="w-full bg-slate-900/50 border border-white/5 rounded-xl py-3 pl-12 pr-4 text-sm text-white focus:outline-none focus:border-blue-500/50" />
                  </div>
                  <div className="flex gap-2">
                    <div className="bg-slate-900 border border-white/5 rounded-lg px-3 py-2 text-xs font-bold text-slate-400 flex items-center gap-2">Filter <SafeIcon icon={FiIcons.FiChevronDown} /></div>
                    <div className="bg-slate-900 border border-white/5 rounded-lg px-3 py-2 text-xs font-bold text-slate-400 flex items-center gap-2">Sort <SafeIcon icon={FiIcons.FiChevronDown} /></div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead className="border-b border-white/5">
                      <tr className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                        <th className="pb-4">Website</th>
                        <th className="pb-4">DR</th>
                        <th className="pb-4">Traffic</th>
                        <th className="pb-4">Category</th>
                        <th className="pb-4">Price</th>
                        <th className="pb-4">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {marketplaceItems.map((item, idx) => (
                        <tr key={idx} className="group">
                          <td className="py-4 text-sm font-bold text-white pr-4">{item.site}</td>
                          <td className="py-4"><span className="bg-blue-500/10 text-blue-400 text-[10px] font-black px-2 py-1 rounded border border-blue-500/20">{item.dr}</span></td>
                          <td className="py-4 text-sm font-bold text-white">{item.traffic}</td>
                          <td className="py-4 text-xs font-medium text-slate-400">{item.cat}</td>
                          <td className="py-4 text-sm font-bold text-emerald-400">{item.price}</td>
                          <td className="py-4"><button className="text-[10px] font-black uppercase tracking-widest text-blue-500 hover:text-blue-400 transition-colors">View Details</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <TestimonialCarousel />

      {/* 4. Why Guest Posts Still Work */}
      <section className="py-24 bg-[#070b14]/30 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">Why Guest Posts Still Matter</h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto leading-relaxed">High-quality guest posts continue to help brands build authority, improve rankings, and increase visibility across search engines.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { t: 'Build Authority', d: 'Earn placements on trusted websites in your niche.', i: FiIcons.FiShield, c: 'blue' },
              { t: 'Improve Rankings', d: 'Contextual backlinks help strengthen your SEO profile.', i: FiIcons.FiTrendingUp, c: 'emerald' },
              { t: 'Increase Visibility', d: 'Appear on websites your customers already trust.', i: FiIcons.FiEye, c: 'purple' },
              { t: 'Diversify Profile', d: 'Build a natural backlink profile across multiple sources.', i: FiIcons.FiLayers, c: 'cyan' }
            ].map((card, idx) => (
              <motion.div key={idx} whileHover={{ y: -5 }} className="glass-card p-8 rounded-2xl border-white/5 bg-[#0F1423] hover:border-white/10 transition-all group">
                <div className={`w-12 h-12 rounded-xl bg-${card.c}-500/10 flex items-center justify-center text-${card.c}-400 mb-6 group-hover:scale-110 transition-transform`}>
                  <SafeIcon icon={card.i} className="text-2xl" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{card.t}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{card.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Filters & Metrics - (Centered Layout previously requested) */}
      <section className="py-24 lg:py-32 relative">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">Advanced Filtering & Transparent Metrics</h2>
          <p className="text-slate-400 text-lg mb-16 max-w-2xl mx-auto leading-relaxed">Find placements that match your exact SEO goals using detailed marketplace filters and real website data.</p>
          
          <div className="flex flex-wrap justify-center gap-4 md:gap-6">
            {[
              { l: 'DR / DA', i: FiIcons.FiBarChart2 }, { l: 'Organic Traffic', i: FiIcons.FiTrendingUp }, { l: 'Country', i: FiIcons.FiGlobe },
              { l: 'Language', i: FiIcons.FiType }, { l: 'Category / Niche', i: FiIcons.FiGrid }, { l: 'Price Range', i: FiIcons.FiTag },
              { l: 'Turnaround Time', i: FiIcons.FiClock }, { l: 'Link Attributes', i: FiIcons.FiLink }, { l: 'Website Type', i: FiIcons.FiLayout }
            ].map((item, idx) => (
              <motion.div 
                key={idx} 
                initial={{ opacity: 0, y: 10 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                viewport={{ once: true }} 
                transition={{ delay: idx * 0.05 }}
                className="flex items-center gap-3 px-6 py-4 rounded-2xl bg-white/5 border border-white/5 hover:border-white/10 hover:bg-white/10 transition-colors"
              >
                <div className="text-blue-400 flex-shrink-0">
                  <SafeIcon icon={item.i} className="text-xl" />
                </div>
                <span className="text-sm md:text-base font-bold text-white leading-tight">{item.l}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. Why PRBoost */}
      <section className="py-24 bg-[#070b14]/50 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">Why Brands Choose PRBoost</h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">Built for businesses and agencies looking for scalable, transparent, and high-quality placements.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { t: 'Real Websites Only', d: 'Zero PBNs or low-quality link farms. We only offer placements on verified, active websites.', i: FiIcons.FiCheckCircle },
              { t: 'Transparent Metrics', d: 'Get access to real-time DR, organic traffic, and ranking data for every single site.', i: FiIcons.FiActivity },
              { t: 'No PBNs', d: 'We strictly vet our inventory to ensure no private blog networks ever touch your campaign.', i: FiIcons.FiShieldOff },
              { t: 'Large Inventory', d: 'Over 100,000 websites across 20+ industries and countries at your fingertips.', i: FiIcons.FiDatabase },
              { t: 'Marketplace Ordering', d: 'A streamlined, self-service platform that makes link building as easy as shopping.', i: FiIcons.FiShoppingCart },
              { t: 'Modern Experience', d: 'A clean, fast, and intuitive dashboard designed for professional SEO teams.', i: FiIcons.FiCpu }
            ].map((f, i) => (
              <div key={i} className="p-8 rounded-2xl glass-card border-white/5 flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-400 mb-6">
                  <SafeIcon icon={f.i} className="text-2xl" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{f.t}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. Agency Section */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24">
            <div className="w-full lg:w-1/2">
              <span className="text-purple-400 font-bold uppercase tracking-[0.2em] text-xs mb-4 block">For Agencies</span>
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">Built for Agencies That Need Scale</h2>
              <p className="text-slate-400 text-lg mb-10 leading-relaxed">Manage placements, organize campaigns, and access high-authority websites from one centralized platform.</p>
              <div className="space-y-6 mb-12">
                {[
                  'Manage all placements in one place',
                  'Access vetted high-authority websites',
                  'Organize campaigns more efficiently',
                  'Scale your outreach and SEO operations'
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-4 items-start">
                    <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0 mt-1 border border-purple-500/30">
                      <SafeIcon icon={FiIcons.FiCheck} className="text-purple-400 text-xs" />
                    </div>
                    <p className="text-white font-bold text-lg">{item}</p>
                  </div>
                ))}
              </div>
              <Button href="https://app.prboost.io/" variant="primary" className="!from-purple-600 !to-indigo-600 shadow-[0_20px_50px_rgba(139,92,246,0.2)]">Start Browsing</Button>
            </div>
            <div className="w-full lg:w-1/2 relative">
              <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="glass-card rounded-[2.5rem] border-white/10 bg-[#121627] p-8 md:p-10 shadow-2xl relative z-10">
                <div className="flex items-center justify-between mb-8">
                  <h3 className="text-lg font-bold text-white">Campaign Management</h3>
                  <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-white/5"></div>
                    <div className="w-3 h-3 rounded-full bg-white/5"></div>
                  </div>
                </div>
                <div className="space-y-6">
                  {[
                    { n: 'Global Tech Outreach', p: 85, s: 'Live' },
                    { n: 'Finance Authority Build', p: 42, s: 'Processing' },
                    { n: 'SaaS Growth Campaign', p: 100, s: 'Completed' }
                  ].map((camp, i) => (
                    <div key={i} className="p-5 rounded-2xl bg-white/5 border border-white/5">
                      <div className="flex justify-between items-center mb-4">
                        <span className="font-bold text-white">{camp.n}</span>
                        <span className={`text-[10px] font-black px-2 py-0.5 rounded uppercase border ${camp.s === 'Live' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : camp.s === 'Processing' ? 'bg-amber-500/10 border-amber-500/20 text-amber-400' : 'bg-blue-500/10 border-blue-500/20 text-blue-400'}`}>
                          {camp.s}
                        </span>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex-grow h-2 bg-slate-900 rounded-full overflow-hidden">
                          <motion.div initial={{ width: 0 }} whileInView={{ width: `${camp.p}%` }} transition={{ duration: 1 }} className={`h-full bg-gradient-to-r ${i === 1 ? 'from-amber-500 to-orange-500' : 'from-blue-500 to-purple-500'}`} />
                        </div>
                        <span className="text-xs font-bold text-white">{camp.p}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
              <div className="absolute -bottom-10 -right-10 w-64 h-64 bg-purple-600/10 blur-[100px] rounded-full pointer-events-none"></div>
            </div>
          </div>
        </div>
      </section>

      {/* 10. FAQ */}
      <section className="py-24 bg-[#070b14]/50 border-y border-white/5">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className={`rounded-2xl border transition-all duration-300 ${openFaq === i ? 'bg-[#1a1f35] border-blue-500/30 ring-1 ring-blue-500/10 shadow-[0_0_30px_rgba(59,130,246,0.1)]' : 'bg-[#121627] border-white/5 hover:bg-[#1a1f35] hover:border-white/10'}`}>
                <button 
                  onClick={() => setOpenFaq(openFaq === i ? null : i)} 
                  className="w-full px-8 py-7 flex items-center justify-between text-left focus:outline-none"
                >
                  <span className={`font-bold text-lg transition-colors ${openFaq === i ? 'text-white' : 'text-slate-300'}`}>{faq.q}</span>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${openFaq === i ? 'bg-blue-500 text-white rotate-180' : 'bg-white/5 text-slate-500 group-hover:bg-white/10'}`}>
                    <SafeIcon icon={FiIcons.FiChevronDown} className="text-lg" />
                  </div>
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div 
                      initial={{ height: 0, opacity: 0 }} 
                      animate={{ height: 'auto', opacity: 1 }} 
                      exit={{ height: 0, opacity: 0 }} 
                      className="overflow-hidden"
                    >
                      <div className="px-8 pb-8 pt-0 text-slate-400 font-medium leading-relaxed text-[17px] border-t border-white/5 mt-2 pt-6 italic">
                        {faq.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 11. Final CTA */}
      <section className="py-32 relative overflow-hidden text-center">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-600/10 blur-[150px] rounded-full pointer-events-none animate-pulse"></div>
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }} 
            className="text-3xl md:text-6xl font-bold text-white mb-8 tracking-tight leading-tight"
          >
            Start Building Authority <br />
            <span className="text-gradient">With Guest Posts</span>
          </motion.h2>
          <p className="text-xl md:text-2xl text-slate-400 mb-12 max-w-3xl mx-auto leading-relaxed">
            Access vetted websites, launch placements, and scale your authority with PRBoost.
          </p>
          <div className="flex justify-center">
            <Button 
              href="https://app.prboost.io/"
              variant="primary" 
              className="w-full sm:w-auto !px-10 !py-4.5 text-lg font-bold shadow-[0_0_50px_rgba(37,99,235,0.4)] hover:shadow-[0_0_70px_rgba(37,99,235,0.6)]"
            >
              Join Now & Start Building Links
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GuestPosts;
